import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { AccountState, selectors } from './state';
import { AccountCommand } from './commands';

/**
 * Command operations — each user-sourced save/submit/delete trigger maps to the
 * backend client command its policy invokes. Capability is DERIVED from the
 * named client method ( `serverClientOperations[name].operation` →
 * `operationCapability` ); no capability string is hand-authored, so re-bucketing
 * a capability on the backend reflows this gate via regeneration. This is the
 * single source of truth for the runtime gate ( threaded into `canApply` via
 * `gateCommandByOperation`; commands absent from the map pass through ).
 *
 * `users/form/submit` is add-while-adding ( AddAccountUser ),
 * update-while-editing ( the edit path issues UpdateUserProfile +
 * UpdateMembershipRoles; gated on UpdateMembershipRoles, the more privileged
 * identity:users:manage ). `groups/form/submit` is create-while-adding,
 * rename-while-renaming, resolved from `assignmentGroups.formMode` (TV1-1878).
 * See `domains/warehouses/state/invariants.ts`.
 */
export const commandOperations: CommandOperationMap<AccountState, AccountCommand> = {
  'account/info/save': 'UpdateAccountInfo',
  'account/billing/form/submit': 'AddBillingEntity',
  'account/users/form/submit': ( state ) =>
    state.users.formMode.type === 'edit' ? 'UpdateMembershipRoles' : 'AddAccountUser',
  'account/rules/submit': 'SetRule',
  'account/rules/delete': 'RemoveRule',
  'account/skuConfig/save/start': 'SetCategorySkuFormulaOverride',
  'account/skuConfig/remove/start': 'RemoveCategorySkuFormulaOverride',
  // Order statuses save is gated by the same account-settings (sales) capability
  // derived from the SetOrderStatuses backend command (WS-2 / TV1-1888).
  'account/orderStatuses/save/start': 'SetOrderStatuses',
  'account/groups/form/openAdd': 'CreateAssignmentGroup',
  'account/groups/form/openRename': 'RenameAssignmentGroup',
  'account/groups/form/submit': ( state ) =>
    state.assignmentGroups.formMode.type === 'rename'
      ? 'RenameAssignmentGroup'
      : 'CreateAssignmentGroup',
  'account/groups/delete': 'DeleteAssignmentGroup'
};

/** Command types gated by the backend-operation capability. */
const OPERATION_GATED_COMMANDS = new Set( Object.keys( commandOperations ));

/**
 * Invariants enforce:
 * 1. Normalization - state consistency
 * 2. Permissions - who can execute which commands
 * 3. Validation - form validation for UI feedback
 */

/**
 * Normalize state for consistency.
 *
 * Ensures:
 * - Current page is within valid bounds for billing and users lists
 * - Form mode is consistent with form data
 */
const normalize = ( state: AccountState ): AccountState => {
  let normalized = { ...state };

  // Ensure billing current page is within bounds
  const billingTotalPages = selectors.billingTotalPages( state );
  if ( billingTotalPages > 0 && normalized.billing.currentPage > billingTotalPages ) {
    normalized = {
      ...normalized,
      billing: {
        ...normalized.billing,
        currentPage: billingTotalPages
      }
    };
  }
  if ( normalized.billing.currentPage < 1 ) {
    normalized = {
      ...normalized,
      billing: {
        ...normalized.billing,
        currentPage: 1
      }
    };
  }

  // Ensure users current page is within bounds
  const usersTotalPages = selectors.usersTotalPages( state );
  if ( usersTotalPages > 0 && normalized.users.currentPage > usersTotalPages ) {
    normalized = {
      ...normalized,
      users: {
        ...normalized.users,
        currentPage: usersTotalPages
      }
    };
  }
  if ( normalized.users.currentPage < 1 ) {
    normalized = {
      ...normalized,
      users: {
        ...normalized.users,
        currentPage: 1
      }
    };
  }

  // If billing form is closed, ensure form errors are reset
  if ( normalized.billing.formMode.type === 'closed' ) {
    normalized = {
      ...normalized,
      billing: {
        ...normalized.billing,
        formErrors: {}
      }
    };
  }

  // If users form is closed, ensure form errors are reset
  if ( normalized.users.formMode.type === 'closed' ) {
    normalized = {
      ...normalized,
      users: {
        ...normalized.users,
        formErrors: {}
      }
    };
  }

  return normalized;
};

/**
 * Permission control - who can execute which commands.
 *
 * Source hierarchy:
 * - 'user': Can do everything except direct data mutations from api
 * - 'ai': Can do most things, but restricted from:
 *   - Direct data mutations (add/update/delete)
 *   - Backend command confirmations (submit)
 * - 'api': Can execute data sync and loading commands
 * - 'application': Can set tab (synced from page)
 * - 'url': Can only modify navigation/filters
 */
const canApply = ({
  state,
  command,
  meta
}: {
  state: AccountState;
  command: AccountCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // Application source: tab sync from page
  if ( source === 'application' ) {
    return type === 'account/tab/set';
  }

  // API source: policies can execute these commands
  if ( source === 'api' ) {
    return (
      // Account data sync
      type === 'account/data/set' ||
      type === 'account/data/clear' ||
      // Account info loading
      type === 'account/info/loading/setSaving' ||
      type === 'account/info/cancel' ||
      // Billing data sync
      type.startsWith( 'account/billing/data/' ) ||
      type.startsWith( 'account/billing/loading/' ) ||
      type === 'account/billing/form/close' ||
      type === 'account/billing/form/setErrors' ||
      // Users data sync
      type.startsWith( 'account/users/data/' ) ||
      type.startsWith( 'account/users/loading/' ) ||
      type === 'account/users/form/close' ||
      type === 'account/users/form/setErrors' ||
      // Assignment groups data sync (TV1-1878) — policies dispatch these
      type.startsWith( 'account/groups/data/' ) ||
      type.startsWith( 'account/groups/loading/' ) ||
      type === 'account/groups/pending/clear' ||
      type === 'account/groups/form/close' ||
      // Rules data sync
      type.startsWith( 'account/rules/definitions/' ) ||
      type.startsWith( 'account/rules/applied/' ) ||
      type.startsWith( 'account/rules/loading/' ) ||
      // SKU Config data sync
      type === 'account/skuConfig/setData' ||
      type === 'account/skuConfig/save/success' ||
      type === 'account/skuConfig/save/error' ||
      type === 'account/skuConfig/remove/success' ||
      type === 'account/skuConfig/remove/error' ||
      // Order Statuses data sync (WS-2 / TV1-1888)
      type === 'account/orderStatuses/setData' ||
      type === 'account/orderStatuses/save/success' ||
      type === 'account/orderStatuses/save/error' ||
      // Top-level loading
      type === 'account/loading/setAccount'
    );
  }

  // URL source: only navigation/filters
  if ( source === 'url' ) {
    return (
      type === 'account/tab/set' ||
      type === 'account/billing/list/setSearch' ||
      type === 'account/billing/list/setPage' ||
      type === 'account/users/list/setSearch' ||
      type === 'account/users/list/setPage'
    );
  }

  // AI source: most things allowed except direct data mutations and submit actions
  if ( source === 'ai' ) {
    // Cannot execute privileged operation-gated commands (TV1-1878:
    // group CRUD/membership + account-member removal).
    if ( OPERATION_GATED_COMMANDS.has( type )) {
      return false;
    }
    // Cannot directly mutate data
    if (
      type.startsWith( 'account/billing/data/' ) ||
      type.startsWith( 'account/users/data/' ) ||
      type.startsWith( 'account/rules/definitions/' ) ||
      type.startsWith( 'account/rules/applied/' )
    ) {
      return false;
    }

    // Cannot execute submit/save actions
    if (
      type === 'account/info/save' ||
      type === 'account/billing/form/submit' ||
      type === 'account/users/form/submit' ||
      type === 'account/rules/submit' ||
      type === 'account/rules/delete' ||
      type === 'account/skuConfig/save/start' ||
      type === 'account/skuConfig/remove/start'
    ) {
      return false;
    }

    return true;
  }

  // User source: everything allowed except direct api commands; the
  // save/submit/delete triggers (including TV1-1878 assignment-group CRUD/
  // membership) are gated by the backend operation declared in
  // commandOperations ( commands absent from the map pass through ).
  if ( source === 'user' ) {
    return gateCommandByOperation( commandOperations, { state, command, meta });
  }

  // Unknown sources: deny
  return false;
};

/**
 * Validate state for UI error display.
 *
 * Only validates form fields when forms are open.
 * Returns null if valid, errors object if invalid.
 */
const validate = ( state: AccountState ): Record<string, string> | null => {
  const errors: Record<string, string> = {};

  // Validate billing form when open
  if ( state.billing.formMode.type !== 'closed' ) {
    const { formData } = state.billing;

    if ( !formData.name.trim()) {
      errors['billing.name'] = 'Nombre comercial es requerido';
    }

    if ( !formData.legalName.trim()) {
      errors['billing.legalName'] = 'Razón social es requerida';
    }

    if ( !formData.documentId.trim()) {
      errors['billing.documentId'] = 'CUIT es requerido';
    }

    if ( !formData.billingAddress.trim()) {
      errors['billing.billingAddress'] = 'Dirección fiscal es requerida';
    }

    if ( !formData.invoicingIntegrationId.trim()) {
      errors['billing.invoicingIntegrationId'] = 'La integración de facturación es requerida';
    }
  }

  // Validate users form when open
  if ( state.users.formMode.type !== 'closed' ) {
    const { formData } = state.users;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if ( !formData.email.trim()) {
      errors['users.email'] = 'Email es requerido';
    } else if ( !emailRegex.test( formData.email.trim())) {
      errors['users.email'] = 'Email no es válido';
    }

    if ( !formData.firstName?.trim()) {
      errors['users.firstName'] = 'Nombre es requerido';
    }

    if ( !formData.lastName?.trim()) {
      errors['users.lastName'] = 'Apellido es requerido';
    }

    if ( !formData.roles || formData.roles.length === 0 ) {
      errors['users.roles'] = 'Al menos un rol es requerido';
    }
  }

  // Validate assignment-group form when open (TV1-1878)
  if ( state.assignmentGroups.formMode.type !== 'closed' ) {
    if ( !state.assignmentGroups.formData.name.trim()) {
      errors['groups.name'] = 'El nombre del grupo es requerido';
    }
  }

  // Return null if no errors
  return Object.keys( errors ).length > 0 ? errors : null;
};

export const invariants: Invariants<AccountState, AccountCommand> = {
  normalize,
  canApply,
  validate
};
